import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  LinearProgress,
  Button,
  Alert,
  AlertTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import { getPixelVisits, startFreeTrial } from '../../../actions/pixel';
import { isEmpty, sortBy, uuidv4 } from '../../../lib/Utils';
import { pixelVisitsSchema } from '../config';
import { AutoRefresh, BdexDataGrid } from '../../Common';
import './styles.css';

function PricingTable() {
  const params = useParams();
  const { pixel_key } = params;
  const { user } = useSelector(state => state.auth);
  const { customer_client_secret, } = useSelector(state => state.data);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/pricing-table.js';
    script.async = true;
    document.body.appendChild(script);
  }, []);

  // customerClientSecret only lasts for 30 min
  const customerClientSecret = customer_client_secret;
  const customerEmail = user.email_address;
  const pricingTableId = process.env.REACT_APP_STRIPE_PRICING_TABLE_ID ?? '';
  const pubishableKey = process.env.REACT_APP_STRIPE_PRICING_PUBLISHABLE_KEY ?? '';
  return (
    <div>
      <stripe-pricing-table
        {...(pixel_key ? { 'client-reference-id': pixel_key } : {})}
        {...(customerClientSecret
          ? { 'customer-session-client-secret': customerClientSecret }
          : { 'customer-email': customerEmail }
        )}
        pricing-table-id={pricingTableId}
        publishable-key={pubishableKey}>
      </stripe-pricing-table>
    </div>
  );
}

export function PixelVisits(props) {

  const { embedded } = props;

  // descending by id desc, i.e. latest first
  //const defaultSort = sortBy('id', 'desc');

  const {
    pixel_visits,
    reporting_enabled,
    reporting_data,
    has_free_trial,
    subscription_history,
    isLoading,
  } = useSelector(state => state.data);

  const { user } = useSelector(state => state.auth);

  const dispatch = useDispatch();

  const params = useParams();
  const { pixel_key } = params;

  const [searchParams] = useSearchParams();

  const [availableRows, setAvailableRows] = useState([]);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState('');
  const initialCallMadeRef = useRef(false);
  const autoSelectionDoneRef = useRef(false);

  const handleGetPixelVisits = (subscriptionId) => {
    setAvailableRows([]);
    dispatch(getPixelVisits({
      pixel_key,
      subscription_history_id: subscriptionId || undefined,
      is_enterprise_user: user.is_enterprise_user,
    }));
  };

  //Reset when switching pixels to avoid cached state
  useEffect(() => {
    setSelectedSubscriptionId('');
    initialCallMadeRef.current = false;
    autoSelectionDoneRef.current = false;
  }, [pixel_key]);

  // Make initial call on mount (without filter)
  useEffect(() => {
    if (!initialCallMadeRef.current) {
      initialCallMadeRef.current = true;
      handleGetPixelVisits();
    }
  }, [pixel_key]);

  // After initial call completes, check subscription_history for auto-selection
  // This runs only once after the initial data loads
  useEffect(() => {
    // Only run if we haven't done auto-selection yet and we have pixel_visits data
    if (!autoSelectionDoneRef.current && !isEmpty(pixel_visits)) {
      autoSelectionDoneRef.current = true;
      
      // Check if there's an active subscription
      if (!isEmpty(subscription_history)) {
        const activeSubscription = subscription_history.find(
          sub => sub.active_flag === 'Y'
        );
        
        if (activeSubscription) {
          // Set the dropdown selection
          setSelectedSubscriptionId(activeSubscription.site_has_reporting_enabled_key);
          // Make a filtered API call
          handleGetPixelVisits(activeSubscription.site_has_reporting_enabled_key);
        }
        // If no active subscription, just keep the initial visits (do nothing)
      }
      // If subscription_history is empty, just keep the initial visits (do nothing)
    }
  }, [pixel_visits, subscription_history]);

  // Handle manual subscription filter changes (user interaction)
  useEffect(() => {
    // Only trigger if auto-selection has been completed
    // This prevents triggering on the initial selectedSubscriptionId state
    if (autoSelectionDoneRef.current && initialCallMadeRef.current) {
      handleGetPixelVisits(selectedSubscriptionId);
    }
  }, [selectedSubscriptionId]);

  useEffect(() => {
    setAvailableRows([]);
    if (!isEmpty(pixel_visits)) {
      setAvailableRows(pixel_visits);
    }
  }, [pixel_visits]);

  let columns = [];
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    () => pixelVisitsSchema.filter(x => x.hide === true).reduce((a, v) => ({ ...a, [v['field']]: false }), {})
  );

  if (!isEmpty(pixel_visits)) {
    columns = Object.keys(pixel_visits[0]).map(x => {
      return { field: x, headerName: x, };
    });

    // set up from the config
    columns = pixelVisitsSchema.filter(x => x.id === pixel_visits[0].id);
  }

  const renderToolbarExport = () => (
    <GridToolbarExport
      csvOptions={{
        fileName: `bdex-omni-pixel-${pixel_key}-${new Date().toISOString().replace(/[-:T\.Z]/g, '')}`,
        delimiter: ',',
        utf8WithBom: true,
      }} />
  );

  const DownloadToolbar = () => (
    <GridToolbarContainer>
      <Grid
        container
        direction='row'
        justifyContent='space-between'
        alignItems='center'>
        <Grid item>
          <FormControl sx={{ minWidth: 300, margin: '5px 0' }}>
            <InputLabel id="subscription-filter-label">Filter by Period</InputLabel>
            <Select
              sx={{ height: '50px' }}
              labelId="subscription-filter-label"
              id="subscription-filter"
              value={selectedSubscriptionId}
              label="Filter by Period"
              onChange={(e) => setSelectedSubscriptionId(e.target.value)}
            >
              <MenuItem value="">
                <em>All visits</em>
              </MenuItem>
              {!isEmpty(subscription_history) && subscription_history.map((subscription) => {
                const startDate = new Date(subscription.ts_start).toLocaleDateString();
                const endDate = new Date(subscription.ts_end).toLocaleDateString();
                const label = `${startDate} - ${endDate}${subscription.active_flag === 'Y' ? ' (Active)' : ''}`;
                return (
                  <MenuItem
                    key={subscription.site_has_reporting_enabled_key}
                    value={subscription.site_has_reporting_enabled_key}
                  >
                    {label}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          {!isEmpty(pixel_visits) ? renderToolbarExport() : null}
        </Grid>
      </Grid>
    </GridToolbarContainer>
  );

  const component = (
    <Grid container id='pixel-get-one-pixel-visits'>
      <Grid item xs={6} sx={{ my: 1 }}>
        <Typography><strong>Pixel Visits</strong></Typography>
      </Grid>      
      <Grid item xs={6} sx={{ my: 1 }}>
        <AutoRefresh handler={() => handleGetPixelVisits(selectedSubscriptionId)} off={true} />
      </Grid>
      {/* <Grid item>
        <Paper>start: {startDate?.toISOString()}</Paper>
        <Paper>end: {endDate?.toISOString()}</Paper>
      </Grid> */}
      { isLoading ? <LinearProgress /> : null }
      <Grid item xs={12} sx={{ boxShadow: 10, borderRadius: 1, mt: 1 }}>
        <BdexDataGrid
          rows={availableRows}
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(x) => setColumnVisibilityModel(x)}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[10,50,100]}
          checkboxSelection={false}
          disableRowSelectionOnClick={true}
          autoHeight={true}
          density={'compact'}
          getRowId={row => uuidv4()}
          slots={{ toolbar: DownloadToolbar, }}
          loading={isLoading}
          sx={{ m: 1, '--DataGrid-overlayHeight': '200px' }} />
      </Grid> 
    </Grid>
  );

  const renderSubscriptionDetails = () => {
    const details = reporting_data;
    // 1. API returns reporting_enabled & customerClientSecret (can be null)
    if (isLoading) return ( <LinearProgress /> );

    const isOnFreeTrial = reporting_enabled && details?.data?.trial_activated;

    if (isOnFreeTrial) {
      const endPeriod = new Date(`${details.ts_end}`).toDateString();
      const limit = Number(details.record_limit).toLocaleString('en-US');
      return (
        <div>
          <Alert severity='success' color='info' >
            {`You are on Free Trial mode until ${endPeriod}. You have ${limit} records unmasked.`}
          </Alert>
          <Typography variant='h5' my={2}>Subscription plans:</Typography>
          <PricingTable />
        </div>
      );
    };

    // Active subscription
    if (reporting_enabled) {
      const isCancelledSubscription = details.data.reason.includes('cancelled');

      const startPeriod = new Date(`${details.ts_start}`).toDateString();
      const endPeriod = new Date(`${details.ts_end}`).toDateString();
      const limit = Number(details.record_limit).toLocaleString('en-US');
      const planType = `Current plan: ${limit} records`;
      const customerPortal = process.env.REACT_APP_STRIPE_BILLING_PORTAL ?? '';

      const text = isCancelledSubscription ? 'finish' : 'auto-renew';
      const alertText = `Your subscription will ${text} next ${endPeriod}.`;

      return (
        <Alert
          severity={isCancelledSubscription ? 'warning' : 'success'}
          color={isCancelledSubscription ? 'warning': 'info'}
          action={
            <Button
              color={isCancelledSubscription ? 'warning' : 'info'}
              href={customerPortal} target='_blank' variant='contained'>
              Manage your suscription
            </Button>
          }>
          <AlertTitle>{planType}. Start date: {startPeriod}</AlertTitle>
          {alertText}
        </Alert>
      )
    }
    // no active subscription - no free trial
    if (!reporting_enabled) {
      return (
        <div>
          <Typography variant='h5' my={2}>Subscription plans:</Typography>
          <PricingTable />
        </div>
      );
    }
  }

  const handleOnClickStartFreeTrial = () => {
    dispatch(startFreeTrial({ pixel_key }));
  };

  const renderFreeTrial = () => {
    if (reporting_enabled || !has_free_trial) return null;
    return (
      <Alert
        severity='success'
        action={
          <Button
            onClick={handleOnClickStartFreeTrial}
            color='success'
            size='small'
            variant='contained'>
            Start free trial
          </Button>
        } >
        Start your free trial, unmask up to 25 visits, and discover the benefits before you decide.
      </Alert>
    )
  }

  return (
    <>
      { !embedded ?
        <Paper id='pixel-visits' sx={{ boxShadow: 10, borderRadius: 1, p: 2 }}>
          {component}
        </Paper>
        : component
      }
      <Box sx={{ display: 'flex', flexFlow: 'column' }} gap={1} my={2}>
        {renderFreeTrial()}
        {renderSubscriptionDetails()}
      </Box>
    </>
  );
}
